import type {ReactElement} from 'react';
import React, {useContext, useEffect, useState} from 'react';
import {Panel, PanelFooter, PanelFooterPrimaryDefaultAction, PanelHeader} from '@Components/panel';
import type {FontData} from '@Libraries/font-library';
import {initFonts} from '@Libraries/font-library';
import {useDefaultPanelId} from '@Panels/hooks/useDefaultPanelId';
import {noop} from '@Utils/general.util';
import {closePanel} from '@Components/panel/panel-reducer';
import useCustomPanelClose from '@Panels/hooks/useCustomPanelClose';
import {ModalDetails} from '@Components/modal/modal-context';
import {StylesPanelColorPickerSection} from '@Panels/styles-input-panel/components/styles-panel-color-picker-section/styles-panel-color-picker-section';
import {useIsInputPanelLoading} from '@Panels/styles-input-panel/styles-input-panel.hooks';
import {StylesInputPanelDefaultSection} from '@Panels/styles-input-panel/components/styles-input-panel-default-section/styles-input-panel-default-section';
import {getAllBrandsWithAssets} from '@Components/mystuff-brands/brands-thunk';
import {FontSelectorPanel} from '@Components/font-selector-panel';
import type {OnBrandItemClickedCallback, OnSelectFontFamilyCallback} from '@Components/font-selector-panel/font-selector.types';
import type {BrandFont} from '@Components/mystuff-brands/brand-kits.types';
import {StylesInputPanelLoading} from '@Panels/styles-input-panel/components/styles-input-panel-loading/styles-input-panel-loading';
import {useHasContentForAllBrandKitsLoaded} from '@Hooks/brands/useHasContentForAllBrandKitsLoaded';
import {useAppDispatch} from '@/hooks';
import type {StylesInputPanelProps} from './styles-input-panel.types';
import {StylesInputPanelGAEventType} from './styles-input-panel.types';
import {STYLES_INPUT_PANEL_ID, StylesInputPanelSection} from './styles-input-panel.types';
import styles from './styles-input-panel.module.scss';
import {onAddBrandAssetClickedHandler} from '@Components/poster-editor/library/poster-editor-library';

export function StylesInputPanel({
  selectedColorHex,
  onSelectColorHex,
  selectedImageURL,
  selectedFontFamily,
  onImageUploadStart = noop,
  onSelectImage = noop,
  onSelectFontFamily = noop,
  allowImageSelection = true,
  onClose = noop,
  className = '',
  gaEventsMap = {},
}: StylesInputPanelProps): ReactElement<any> {
  const [currentSection, setCurrentSection] = useState(StylesInputPanelSection.DEFAULT);
  const id = useDefaultPanelId(STYLES_INPUT_PANEL_ID);
  const dispatch = useAppDispatch();
  const modalDetailsContext = useContext(ModalDetails);
  const isInModal = modalDetailsContext !== null;
  const isLoading = useIsInputPanelLoading();
  const hasBrandKitContentLoaded = useHasContentForAllBrandKitsLoaded();

  const close = (): void => {
    onClose();
    dispatch(closePanel(id));
  };

  const customClose = useCustomPanelClose(id, close);

  const getHeading = (): string => {
    if (currentSection === StylesInputPanelSection.COLORS) {
      return window.i18next.t('pmwjs_color');
    }

    if (currentSection === StylesInputPanelSection.FONTs) {
      return window.i18next.t('pmwjs_heading_font');
    }

    return window.i18next.t('pmwjs_styles');
  };

  const shouldBackButtonBeShown = (): boolean => {
    return currentSection !== StylesInputPanelSection.DEFAULT;
  };

  const onBackButton = (): void => {
    setCurrentSection(StylesInputPanelSection.DEFAULT);
  };

  const onApplyBrandKit = (colorHex?: string, fontFamily?: string, imageURL?: string): void => {
    if (colorHex) {
      onSelectColorHex(colorHex);
    }

    if (fontFamily) {
      onSelectFontFamily(fontFamily);
    }

    if (imageURL && onSelectImage) {
      onSelectImage(imageURL);
    }

    if (gaEventsMap[StylesInputPanelGAEventType.BRAND_KIT]) {
      window.PMW.gtm.trackGA4CustomEvent(gaEventsMap[StylesInputPanelGAEventType.BRAND_KIT]);
    }
  };

  const onSelectFont: OnSelectFontFamilyCallback = (font: FontData): void => {
    onSelectFontFamily(font.fontFamily);
    if (gaEventsMap[StylesInputPanelGAEventType.FONT]) {
      window.PMW.gtm.trackGA4CustomEvent(gaEventsMap[StylesInputPanelGAEventType.FONT]);
    }
  };

  const onSelectColor = (colorHex: string): void => {
    onSelectColorHex(colorHex);

    if (gaEventsMap[StylesInputPanelGAEventType.COLOR]) {
      window.PMW.gtm.trackGA4CustomEvent(gaEventsMap[StylesInputPanelGAEventType.COLOR]);
    }
  }

  const onSelectImageURL = (imgURL: string): void => {
    onSelectImage(imgURL);

    if (gaEventsMap[StylesInputPanelGAEventType.IMAGE]) {
      window.PMW.gtm.trackGA4CustomEvent(gaEventsMap[StylesInputPanelGAEventType.IMAGE]);
    }
  }

  // there are two BrandFont types here, suprressing it till we fix the one used in the editor
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const onSelectBrandFont: OnBrandItemClickedCallback = (brandFont: BrandFont): void => {
    onSelectFontFamily(brandFont.fontFamily);
  };

  const getPanelSpacingClass = (): string => {
    if (currentSection !== StylesInputPanelSection.DEFAULT) {
      return isInModal ? 'spacing-p-4' : 'spacing-p-5';
    }

    return isInModal ? 'spacing-p-l-4 spacing-p-b-4 spacing-p-t-4' : 'spacing-p-l-5 spacing-p-b-5 spacing-p-t-5';
  };

  const getContent = (): ReactElement<any> => {
    if (isLoading) {
      return <StylesInputPanelLoading />;
    }
    return (
      <div className={`flex-v-row ${className} ${getPanelSpacingClass()} ${styles.panelContentWrapper}`}>
        {currentSection === StylesInputPanelSection.DEFAULT ? (
          <StylesInputPanelDefaultSection
            changeSection={setCurrentSection}
            selectedColorHex={selectedColorHex}
            selectedFontFamily={selectedFontFamily}
            className={className}
            allowImageSelection={allowImageSelection}
            onImageUploadStart={onImageUploadStart}
            selectedImageURL={selectedImageURL}
            onApplyBrandKit={onApplyBrandKit}
            onSelectImage={onSelectImageURL}
            closePanel={customClose}
          />
        ) : null}

        {currentSection === StylesInputPanelSection.COLORS ? <StylesPanelColorPickerSection selectedColor={selectedColorHex} onColorHexChange={onSelectColor} /> : null}
        {currentSection === StylesInputPanelSection.FONTs ? (
          <FontSelectorPanel
            className={`_unpadded ${styles.fontSelectorPanel}`}
            selectedFontFamily={selectedFontFamily}
            onSelectFontFamily={onSelectFont}
            onBrandItemClicked={onSelectBrandFont}
            showDividerAfterBrandsSection={false}
            showFontsInDesign={false}
            showBackButton={false}
            showHeader={false}
          />
        ) : null}
      </div>
    );
  };

  const onFooterUpdateBtnClick = (): void => {
    setCurrentSection(StylesInputPanelSection.DEFAULT);
  };

  const getFooter = (): ReactElement<any> | null => {
    return currentSection !== StylesInputPanelSection.DEFAULT ? (
      <PanelFooter
        className={styles.footer}
        fullWidthPrimaryActions
        primaryActions={[<PanelFooterPrimaryDefaultAction key="done-cta" text={window.i18next.t('pmwjs_done')} onClick={onFooterUpdateBtnClick} />]}
      />
    ) : null;
  };

  useEffect(() => {
    void initFonts();
    if (!hasBrandKitContentLoaded) {
      void dispatch(getAllBrandsWithAssets());
    }
  }, []);

  return currentSection === StylesInputPanelSection.FONTs ? (
    <FontSelectorPanel
      className={`spacing-p-5 ${styles.stylesInputPanel}`}
      selectedFontFamily={selectedFontFamily}
      onSelectFontFamily={onSelectFont}
      onBrandItemClicked={onSelectBrandFont}
      onBackButtonCallback={onBackButton}
      onAddBrandAsset={onAddBrandAssetClickedHandler}
      onCloseButtonCallback={customClose}
      headerText={window.i18next.t('pmwjs_heading_font')}
      showFontsInDesign={false}
      showDividerAfterBrandsSection={false}
      panelFooter={getFooter()}
      scrollToTopButtonClassName={styles.scrollToTopButton}
      showCloseButton
      showBackButton
      showHeader
    />
  ) : (
    <Panel
      panelHeader={<PanelHeader title={getHeading()} onClose={customClose} centerAlignTitle showBackButton={shouldBackButtonBeShown()} onBackButton={onBackButton} />}
      panelContent={getContent()}
      panelFooter={getFooter()}
    />
  );
}
